import React, { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { Box } from "@mui/material";

const VERT = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position, 1.0);
  }
`;

const FRAG = `
  precision highp float;

  uniform float uTime;
  uniform vec2 uResolution;
  uniform vec3 uCanvas;
  uniform vec3 uInk;
  uniform vec3 uAccent;
  uniform vec3 uWarm;

  varying vec2 vUv;

  // hash + value noise
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
  }

  float noise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }

  float fbm(vec2 p) {
    float v = 0.0;
    float a = 0.5;
    for (int i = 0; i < 4; i++) {
      v += a * noise(p);
      p *= 2.0;
      a *= 0.5;
    }
    return v;
  }

  void main() {
    vec2 uv = vUv;
    float aspect = uResolution.x / max(uResolution.y, 1.0);
    uv.x *= aspect;

    float t = uTime * 0.04;

    // domain-warped fbm
    vec2 p = uv * 1.4;
    vec2 q = vec2(
      fbm(p + vec2(0.0, t)),
      fbm(p + vec2(5.2, 1.3 - t))
    );
    vec2 r = vec2(
      fbm(p + 3.0 * q + vec2(1.7, 9.2) + 0.18 * t),
      fbm(p + 3.0 * q + vec2(8.3, 2.8) - 0.12 * t)
    );
    float f = fbm(p + 3.0 * r);

    // warm cream base, blended into ink shadows
    vec3 col = mix(uCanvas, uWarm, smoothstep(0.15, 0.75, f));
    // ink contour for depth
    col = mix(col, uInk, smoothstep(0.65, 0.95, length(r)) * 0.35);
    // accent highlight where the second warp peaks
    col = mix(col, uAccent, smoothstep(0.55, 0.95, q.x) * 0.55);

    // soft vignette pulled toward bottom so it fades into the page
    float vy = smoothstep(1.0, 0.15, vUv.y);
    float vx = smoothstep(0.0, 0.35, vUv.x) * smoothstep(1.0, 0.65, vUv.x);
    float fade = mix(0.4, 1.0, vy * mix(0.8, 1.0, vx));

    col = mix(uCanvas, col, fade);

    gl_FragColor = vec4(col, 1.0);
  }
`;

const HEX_TO_RGB = hex => {
  const h = hex.replace("#", "");
  return new THREE.Vector3(
    parseInt(h.slice(0, 2), 16) / 255,
    parseInt(h.slice(2, 4), 16) / 255,
    parseInt(h.slice(4, 6), 16) / 255
  );
};

function ShaderPlane() {
  const matRef = useRef();
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uCanvas: { value: HEX_TO_RGB("#f6f6f4") },
      uInk: { value: HEX_TO_RGB("#0f1121") },
      uAccent: { value: HEX_TO_RGB("#3854e8") },
      uWarm: { value: HEX_TO_RGB("#e4decf") }
    }),
    []
  );

  useFrame((state, delta) => {
    if (!matRef.current) return;
    matRef.current.uniforms.uTime.value += delta;
    const { width, height } = state.size;
    matRef.current.uniforms.uResolution.value.set(width, height);
  });

  return (
    <mesh frustumCulled={false}>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        ref={matRef}
        vertexShader={VERT}
        fragmentShader={FRAG}
        uniforms={uniforms}
        depthTest={false}
        depthWrite={false}
        transparent={false}
      />
    </mesh>
  );
}

const HeroBackground = React.memo(function HeroBackground({
  height = "min(720px, 92vh)"
}) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        height,
        width: "100%",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: 0
      }}
    >
      <Canvas
        orthographic
        dpr={[1, 1.5]}
        gl={{
          antialias: false,
          alpha: false,
          powerPreference: "high-performance",
          preserveDrawingBuffer: false
        }}
        style={{ width: "100%", height: "100%", display: "block" }}
      >
        <ShaderPlane />
      </Canvas>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(to bottom, rgba(246, 246, 244, 0) 55%, rgba(246, 246, 244, 0.85) 92%, rgba(246, 246, 244, 1) 100%)",
          pointerEvents: "none"
        }}
      />
    </Box>
  );
});

export default HeroBackground;
